import { MIN_SELECTION_PX, Point, Rect, Size } from './support-image-editor.geometry';

/** Resize-handle van het snip-kader: vier hoeken en vier zijmiddens, benoemd als windrichting. */
export type SnipHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export const SNIP_HANDLES: readonly SnipHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

/** CSS-cursor die bij een handle hoort. */
export function handleCursor(handle: SnipHandle): string {
  switch (handle) {
    case 'nw':
    case 'se':
      return 'nwse-resize';
    case 'ne':
    case 'sw':
      return 'nesw-resize';
    case 'n':
    case 's':
      return 'ns-resize';
    default:
      return 'ew-resize';
  }
}

/**
 * Verschuift de zijde(n) van `handle` met (dx, dy). Een zijde stopt op de
 * viewportrand en komt nooit dichter dan `min` bij de tegenoverliggende zijde,
 * zodat het kader niet omklapt of onder de minimumgrootte zakt.
 */
export function resizeRect(rect: Rect, handle: SnipHandle, dx: number, dy: number, bounds: Size, min = MIN_SELECTION_PX): Rect {
  let left = rect.x;
  let top = rect.y;
  let right = rect.x + rect.width;
  let bottom = rect.y + rect.height;
  if (handle.includes('w')) left = clamp(left + dx, 0, right - min);
  if (handle.includes('e')) right = clamp(right + dx, left + min, bounds.width);
  if (handle.includes('n')) top = clamp(top + dy, 0, bottom - min);
  if (handle.includes('s')) bottom = clamp(bottom + dy, top + min, bounds.height);
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/** Verplaatst het kader met (dx, dy) en houdt het volledig binnen `bounds`; de afmetingen blijven gelijk. */
export function moveRect(rect: Rect, dx: number, dy: number, bounds: Size): Rect {
  return {
    x: clamp(rect.x + dx, 0, Math.max(0, bounds.width - rect.width)),
    y: clamp(rect.y + dy, 0, Math.max(0, bounds.height - rect.height)),
    width: rect.width,
    height: rect.height,
  };
}

export interface ConfirmButtonPosition extends Point {
  /** true als de knop binnen het kader staat omdat er buiten geen plaats is. */
  inside: boolean;
}

/**
 * Plaats van de bevestigknop: diagonaal rechtsonder net buiten het kader (op
 * `margin`), of, als hij daar rechts of onder buiten de viewport zou vallen,
 * binnen het kader in de rechteronderhoek.
 */
export function confirmButtonPosition(rect: Rect, bounds: Size, button: Size, margin: number): ConfirmButtonPosition {
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;
  const outside = { x: right + margin, y: bottom + margin };
  if (outside.x + button.width <= bounds.width && outside.y + button.height <= bounds.height) {
    return { ...outside, inside: false };
  }
  return {
    x: Math.max(rect.x, right - margin - button.width),
    y: Math.max(rect.y, bottom - margin - button.height),
    inside: true,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
